import { useCallback } from "react"
import { Collapse } from "components/ui/collapse/Collapse"
import {
	flattenModelNumberToRows,
	isNestedCategory,
	isModelNumberArray,
} from "components/pages/PageConfiguration/layout/ModelNumbers/layout/TableModelNumbers/TableModelNumbers.helper"
import { Table } from "components/ui/tables/Table/Table"
import type { ColumnsType } from "antd/es/table"
import type { TModelNumbersData, TModelNumberTableRow, TModelNumber } from "types/modelNumbers.type"
import styles from "./TableModelNumbers.module.scss"

const { collapseLabel, collapseBadge, customBorderedTable } = styles

// Count total items in a subcategory
const countItemsInSubCategory = (modelNumbers: TModelNumber[]): number => {
	return modelNumbers.length
}

// Count total items in all subcategories
const countItemsInNestedCategory = (subCategory: { [key: string]: TModelNumber[] }): number => {
	return Object.values(subCategory).reduce((total, modelNumbers) => {
		return total + countItemsInSubCategory(modelNumbers)
	}, 0)
}

// Count total items in the entire nested structure
const countItemsInTopLevel = (nestedCategory: { [key: string]: { [key: string]: TModelNumber[] } }): number => {
	return Object.values(nestedCategory).reduce((total, subCategory) => {
		return total + countItemsInNestedCategory(subCategory)
	}, 0)
}

type Props = {
	columns: ColumnsType<TModelNumberTableRow>
	onRowHandle: (record: TModelNumberTableRow) => { onClick: () => void }
}

export const useCollapseModelNumbers = ({ columns, onRowHandle }: Props) => {
	// Generate third level collapse items (subcategories with table)
	const generateThirdLevelCollapseItems = useCallback(
		(subCategory: { [key: string]: TModelNumber[] }) => {
			return Object.entries(subCategory).map(([subCategoryName, modelNumbers]) => {
				const tableData = modelNumbers ? modelNumbers.flatMap(flattenModelNumberToRows) : []
				const count = tableData.length

				return {
					key: subCategoryName,
					label: (
						<span className={collapseLabel}>
							<span>{subCategoryName}</span>
							<span className={collapseBadge}>{count}</span>
						</span>
					),
					"data-testid": "collapse-third-level",
					children: (
						<Table
							columns={columns}
							data={tableData}
							scrollY={400}
							showHeader={false}
							className={customBorderedTable}
							pagination={false}
							onRow={onRowHandle}
						/>
					),
				}
			})
		},
		[columns, onRowHandle],
	)

	// Generate second level collapse items (nested categories)
	const generateSecondLevelCollapseItems = useCallback(
		(nestedCategory: { [key: string]: { [key: string]: TModelNumber[] } }, topLevelKey: string) => {
			return Object.entries(nestedCategory).map(([nestedCategoryName, subCategory]) => {
				const count = countItemsInNestedCategory(subCategory)

				return {
					key: `${topLevelKey}-${nestedCategoryName}`,
					label: (
						<span className={collapseLabel}>
							<span>{nestedCategoryName}</span>
							<span className={collapseBadge}>{count}</span>
						</span>
					),
					"data-testid": "collapse-second-level",
					children: (
						<div>
							<Collapse items={generateThirdLevelCollapseItems(subCategory)} />
						</div>
					),
				}
			})
		},
		[generateThirdLevelCollapseItems],
	)

	// Generate top level collapse items
	const generateCollapseItems = useCallback(
		(modelNumbersData: TModelNumbersData) => {
			if (!modelNumbersData) return []

			return Object.entries(modelNumbersData).map(([topLevelCategory, value]) => {
				// Handle "Without category" case (array of model numbers)
				if (topLevelCategory === "Without category" && isModelNumberArray(value)) {
					const tableData = value.flatMap(flattenModelNumberToRows)
					const count = tableData.length

					return {
						key: topLevelCategory,
						label: (
							<span className={collapseLabel}>
								<span>{topLevelCategory}</span>
								<span className={collapseBadge}>{count}</span>
							</span>
						),
						"data-testid": "collapse-top-level",
						children: (
							<Table
								columns={columns}
								data={tableData}
								scrollY={600}
								showHeader={false}
								className={customBorderedTable}
								pagination={false}
								onRow={onRowHandle}
							/>
						),
					}
				}

				// Handle nested categories
				if (isNestedCategory(value)) {
					const count = countItemsInTopLevel(value)

					return {
						key: topLevelCategory,
						label: (
							<span className={collapseLabel}>
								<span>{topLevelCategory}</span>
								<span className={collapseBadge}>{count}</span>
							</span>
						),
						"data-testid": "collapse-top-level",
						children: (
							<div>
								<Collapse items={generateSecondLevelCollapseItems(value, topLevelCategory)} />
							</div>
						),
					}
				}

				// Fallback for unexpected structure
				return {
					key: topLevelCategory,
					label: topLevelCategory,
					"data-testid": "collapse-top-level",
					children: <div>No data available</div>,
				}
			})
		},
		[columns, generateSecondLevelCollapseItems, onRowHandle],
	)

	return { generateCollapseItems }
}
